import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../i18n/contentLocales';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { listStations } from '../api/content';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/useAuth';
import type { MultilingualString, Station } from '../api/types';
import ErrorAlert from './ErrorAlert';
import LoadingSpinner from './LoadingSpinner';

interface ImportStationDialogProps {
  open: boolean;
  /** Station ids already linked to the rallye — shown as disabled. */
  linkedStationIds: string[];
  onCancel: () => void;
  onImport: (stationId: string) => Promise<void> | void;
}

const ImportStationDialog: React.FC<ImportStationDialogProps> = ({
  open,
  linkedStationIds,
  onCancel,
  onImport,
}) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('content');
  const orgSlug = user?.organization_slug;

  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listStations(orgSlug, { limit: 200 });
      setStations(resp.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(null);
      void load();
    }
  }, [open, load]);

  const linked = useMemo(() => new Set(linkedStationIds), [linkedStationIds]);

  const visible = useMemo(
    () =>
      stations.filter((s) => {
        if (!search) return true;
        return titleOf(s.title).toLowerCase().includes(search.toLowerCase());
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stations, search, activeLang],
  );

  const handleImport = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await onImport(selected);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('station.import.title')}</DialogTitle>
      <DialogContent>
        <TextField
          label={t('station.import.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2, mt: 1 }}
        />
        {error && <ErrorAlert message={error} />}
        {loading ? (
          <LoadingSpinner />
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            {t('station.import.empty')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={t('station.import.title')}>
              <TableHead>
                <TableRow>
                  <TableCell scope="col">{t('station.list.table.name')}</TableCell>
                  <TableCell scope="col" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {t('station.list.table.address')}
                  </TableCell>
                  <TableCell scope="col">{t('station.list.table.usage')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((s) => {
                  const isLinked = linked.has(s.id);
                  const rallyeTitles = (s.usage?.rallye_titles ?? [])
                    .map((rt) => titleOf(rt))
                    .filter(Boolean);
                  return (
                    <TableRow
                      key={s.id}
                      hover={!isLinked}
                      selected={selected === s.id}
                      onClick={() => !isLinked && setSelected(s.id)}
                      sx={{
                        cursor: isLinked ? 'default' : 'pointer',
                        opacity: isLinked ? 0.5 : 1,
                      }}
                      aria-disabled={isLinked}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {titleOf(s.title) || s.id}
                        {isLinked && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('station.import.alreadyLinked')})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: 'none', sm: 'table-cell' }, color: 'text.secondary' }}
                      >
                        {s.address ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {rallyeTitles.length > 0
                          ? rallyeTitles.join(', ')
                          : t('station.list.table.unused')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!selected || submitting}
        >
          {t('station.import.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportStationDialog;
